import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router';

import type { AuthClient } from './client';
import { onSignInRefused, redirectToSignIn } from './redirect';
import { SignedOut, SigningIn } from './screens';

/** `refused`: the loop guard stopped an automatic redirect, so the person is asked to sign in. */
export type IdentityStatus = 'off' | 'checking' | 'signed-in' | 'signed-out' | 'refused';

export interface Identity {
  /** False in the mock and in a build without the Okta variables: no sign-in, the typed name. */
  enabled: boolean;
  status: IdentityStatus;
  signedIn: boolean;
  /**
   * Redirect to Okta, to come back to `path`. False when the loop guard refused an automatic
   * redirect; a person's own click passes `force`.
   */
  signIn(path: string, force?: boolean): boolean;
  /** Finish the sign-in at the callback; says which page to go to. */
  completeSignIn(): Promise<string>;
  signOut(): void;
}

const OFF: Identity = {
  enabled: false,
  status: 'off',
  signedIn: false,
  signIn: () => false,
  completeSignIn: () => Promise.resolve('/'),
  signOut: () => {},
};

const IdentityContext = createContext<Identity>(OFF);

/** Without a provider, and with no client, sign-in is off: every screen and test as before. */
export function useIdentity(): Identity {
  return useContext(IdentityContext);
}

export function AuthProvider({
  client,
  children,
}: {
  client: AuthClient | null;
  children: ReactNode;
}) {
  const [status, setStatus] = useState<Exclude<IdentityStatus, 'off'>>('checking');
  // The check at load may answer after the callback has finished; the callback wins.
  const completed = useRef(false);

  useEffect(() => {
    if (!client) return;
    client.isAuthenticated().then(
      (ok) => {
        if (!completed.current) setStatus(ok ? 'signed-in' : 'signed-out');
      },
      () => {
        if (!completed.current) setStatus('signed-out');
      },
    );
  }, [client]);

  // The HTTP client signs in again outside React: when the guard declines, the person sees why.
  useEffect(() => {
    if (!client) return;
    return onSignInRefused(() => setStatus('refused'));
  }, [client]);

  const value = useMemo<Identity>(() => {
    if (!client) return OFF;
    return {
      enabled: true,
      status,
      signedIn: status === 'signed-in',
      signIn: (path, force = false) => {
        const started = redirectToSignIn(client, path, force);
        if (!started) setStatus('refused');
        return started;
      },
      completeSignIn: async () => {
        const path = await client.completeSignIn();
        completed.current = true;
        setStatus('signed-in');
        return path;
      },
      signOut: () => {
        client.signOut().catch((error: unknown) => console.error('Sign-out failed.', error));
      },
    };
  }, [client, status]);

  return <IdentityContext.Provider value={value}>{children}</IdentityContext.Provider>;
}

/**
 * The console behind sign-in: a visitor who is not signed in goes to Okta and comes back to the
 * same page. Where sign-in is off it renders its children untouched.
 */
export function RequireSignIn({ children }: { children: ReactNode }) {
  const { enabled, status, signIn } = useIdentity();
  const { pathname, search, hash } = useLocation();
  const path = `${pathname}${search}${hash}`;
  // StrictMode runs the effect twice; the redirect is asked for once.
  const asked = useRef(false);

  useEffect(() => {
    if (status !== 'signed-out' || asked.current) return;
    asked.current = true;
    signIn(path);
  }, [status, signIn, path]);

  if (!enabled || status === 'signed-in') return children;
  if (status === 'refused') return <SignedOut onSignIn={() => signIn(path, true)} />;
  return <SigningIn />;
}
